import RiskSummary from '@/components/risk-summary/risk-summary';
import { Badge, Box, Divider, Flex, Text } from '@cmpsr/components';
import { formatRiskDecimal } from '@/utils';
import { InfoPopover } from '@/components/info-popover/info-popover';

type SingleCancerSummaryProps = {
  currentRisk: number;
  potentialRisk: number;
  averageRisk: number;
  decadeRisk: string;
  averageAgeOnset: string;
  fiveYearSurvivalRate: string;
  cancerType: string;
};

const SingleCancerBadge = ({ children }: { children: React.ReactNode }) => {
  return (
    <Badge
      w="50px"
      color="primary-default"
      p="spacer-2"
      borderRadius="radii-3"
      display="flex"
      backgroundColor="white"
      justifyContent="center"
    >
      {children}
    </Badge>
  );
};

export const SingleCancerSummary = ({
  currentRisk,
  potentialRisk,
  averageRisk,
  decadeRisk,
  averageAgeOnset,
  fiveYearSurvivalRate,
  cancerType,
}: SingleCancerSummaryProps) => {
  return (
    <Box backgroundColor="#F0F5F6" padding="4" borderRadius="radii-3">
      <RiskSummary
        currentRisk={currentRisk}
        potentialRisk={potentialRisk}
        average={averageRisk}
        cancerType={cancerType}
      />

      <Divider my="4" />
      <Flex justifyContent="space-between" mb="4" flexDirection="column" gap="4">
        <Flex gap="1" alignItems="center" justifyContent="space-between">
          <Text variant={{ base: 'text-body-meta-regular', md: 'text-body-regular' }}>
            Your risk in the next 10 years
          </Text>
          <SingleCancerBadge>{formatRiskDecimal(decadeRisk)}</SingleCancerBadge>
        </Flex>
        {cancerType.toLowerCase() !== 'overall' && (
          <>
            <Flex gap="1" alignItems="center" justifyContent="space-between">
              <Text variant={{ base: 'text-body-meta-regular', md: 'text-body-regular' }}>Average age at onset</Text>
              <SingleCancerBadge>{averageAgeOnset}</SingleCancerBadge>
            </Flex>
            <Flex gap="1" justifyContent="space-between">
              <Flex alignItems="center" gap="1">
                <Text variant={{ base: 'text-body-meta-regular', md: 'text-body-regular' }}>
                  5-Year survival when detected at localized state{' '}
                </Text>
                <InfoPopover
                  popoverContent="Based on data from the National Cancer Institute SEER program"
                  color="primary-default"
                  iconSize="s"
                />
              </Flex>
              <SingleCancerBadge>{fiveYearSurvivalRate}</SingleCancerBadge>
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
